import React from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatIDR, formatDate, QTN_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function QuotationsPage() {
  const [rows, setRows] = React.useState([]);
  const [selected, setSelected] = React.useState([]);
  const load = React.useCallback(async () => { const { data } = await api.get("/quotations"); setRows(data); setSelected([]); }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleAll = () => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id));

  const bulkDelete = async () => {
    try { const { data } = await api.post("/quotations/bulk-delete", { ids: selected }); toast.success(`${data.deleted} quotation dibatalkan`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Quotation</h1>
          <p className="text-sm text-neutral-500">Semua penawaran ke pelanggan.</p>
        </div>
        {selected.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="bulk-delete-quotations">
                Hapus {selected.length} Terpilih
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Batalkan {selected.length} quotation?</AlertDialogTitle>
                <AlertDialogDescription>Quotation akan diubah statusnya menjadi Dibatalkan.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-quotations">Ya, Batalkan</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="w-8"><input type="checkbox" checked={selected.length === rows.length && rows.length > 0} onChange={toggleAll} data-testid="select-all-quotations" /></th>
            <th className="text-left">No. Quotation</th>
            <th className="text-left">Pelanggan</th>
            <th className="text-left">Project</th>
            <th className="text-left">Tanggal</th>
            <th className="text-left">Berlaku s/d</th>
            <th className="text-right">Total</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.id} data-testid={`qtn-row-${q.id}`}>
                <td><input type="checkbox" checked={selected.includes(q.id)} onChange={() => toggle(q.id)} data-testid={`select-qtn-${q.id}`} /></td>
                <td className="font-mono text-xs">{q.number}</td>
                <td data-testid={`qtn-customer-${q.id}`}>{q.customer_name || "-"}</td>
                <td>{q.project_name || "-"}</td>
                <td className="text-xs">{formatDate(q.issue_date)}</td>
                <td className="text-xs">{formatDate(q.valid_until)}</td>
                <td className="text-right font-medium">{formatIDR(q.total)}</td>
                <td><StatusBadge statusMap={QTN_STATUS} value={q.status} /></td>
                <td className="text-right"><Link to={`/quotations/${q.id}`} className="text-green-700 text-xs hover:underline">Detail →</Link></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-sm text-neutral-500">Belum ada quotation.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
